// Lumi uses RELATIVE URLs so requests always go to the same origin the user is on.
// This avoids cross-origin cookies/CORB issues that broke login persistence and image URLs.
// Works on preview + production + any custom domain — no env var dependency.
export const LUMI_API = "/api/lumi";
const TOKEN_KEY = "lumi_session_token";

export const setLumiToken = (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch {} };
export const getLumiToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
export const clearLumiToken = () => { try { localStorage.removeItem(TOKEN_KEY); } catch {} };

async function handle(res) {
  if (res.status === 401) {
    const data = await safeJson(res);
    throw Object.assign(new Error("not_authenticated"), { status: 401, data });
  }
  if (res.status === 403) {
    const data = await safeJson(res);
    throw Object.assign(new Error("forbidden"), { status: 403, data });
  }
  if (!res.ok) {
    const data = await safeJson(res);
    throw Object.assign(new Error(data?.detail || `HTTP ${res.status}`), { status: res.status, data });
  }
  return res.json();
}

async function safeJson(res) {
  try { return await res.json(); } catch { return null; }
}

function authHeaders() {
  const t = getLumiToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export const lumiFetch = (path, opts = {}) => fetch(`${LUMI_API}${path}`, {
  credentials: "include",
  headers: { "Content-Type": "application/json", ...authHeaders(), ...(opts.headers || {}) },
  ...opts,
}).then(handle);

export const lumiUpload = (path, formData) => fetch(`${LUMI_API}${path}`, {
  method: "POST",
  credentials: "include",
  headers: authHeaders(),
  body: formData,
}).then(handle);

// UK locale, DD/MM/YYYY 24h. Single source of truth — every Lumi UI must use these.
const _fmtFull = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: false,
});
const _fmtShort = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
});
// Backend datetimes come back from MongoDB as naive ISO strings (no `Z` / no `+00:00`)
// because BSON drops tzinfo. JavaScript would parse them as LOCAL time, which is wrong —
// our backend always stores UTC. So we append `Z` if missing before parsing.
const _parseDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(input)) {
    return new Date(input + "Z");  // naive → assume UTC
  }
  return new Date(input);
};
export const fmtDateTime = (input) => {
  const d = _parseDate(input);
  return d && !isNaN(d.getTime()) ? _fmtFull.format(d) : "";
};
export const fmtDateTimeShort = (input) => {
  const d = _parseDate(input);
  return d && !isNaN(d.getTime()) ? _fmtShort.format(d) : "";
};
// For datetime-local inputs we need YYYY-MM-DDTHH:MM in LOCAL time (browser TZ).
// Accepts either a Date instance or a backend ISO string (naive UTC supported).
export const toLocalInputValue = (input) => {
  const date = _parseDate(input);
  if (!date || isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
